import React, { useContext, PropsWithChildren } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import styled, { ThemeContext } from 'styled-components'
import Axios from 'axios'
import { Oval } from 'svg-loaders-react'
import { Button } from '../Button'
import { AlertContext, AlertType } from '../../context/alertContext'
import { capterraTrackingListener } from './capterraTracking'

const SignupSchema = Yup.object().shape({
  firstName: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
  lastName: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Required'),
  company: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!'),
  email: Yup.string().email('Invalid email').required('Required'),
  city: Yup.string().min(1, 'Too Short!'),
  countryRegion: Yup.string().min(1, 'Too Short!').required('Required'),
  phone: Yup.string().min(5, 'Too Short!').max(11, 'Too Long!'),
})

type FormName =
  | 'start-a-demo'
  | 'contact'
  | 'white-paper'
  | 'events-dr-claire'
  | 'daisee-ebook'
  | 'discovery-vds'
  | 'g-summit'

type DropdownElementDetails = {
  value: string
  text: string
}

const DropdownElements: DropdownElementDetails[] = [
  { value: '', text: '- Select -' },
  { value: 'Australia', text: 'Australia' },
  { value: 'Europe', text: 'Europe' },
  { value: 'United Kingdom', text: 'United Kingdom' },
  { value: 'United States', text: 'United States' },
]

interface HOCProps {
  setSubmitted?: (submitted: boolean) => void
  setUserName?: (name: string) => void
  state?: any
  formName: FormName
  successMessage?: string
  errorMessage: string
  whitefields?: boolean
  action?: string
  isGtmForm?: boolean
  emailSubject?: string
  callback?: () => void
}

export const encode = (data: any) => {
  return Object.keys(data)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&')
}

export default ({
  setSubmitted,
  setUserName,
  state,
  formName,
  successMessage,
  errorMessage,
  whitefields,
  action,
  isGtmForm,
  callback,
  emailSubject,
}: PropsWithChildren<HOCProps>) => {
  const themeContext = useContext(ThemeContext)
  const { setAlertMessage } = useContext(AlertContext)

  return (
    <FormWrapper>
      <Formik
        initialValues={{
          email: state?.email || '',
          firstName: '',
          lastName: '',
          company: '',
          phone: '',
          city: '',
          countryRegion: '',
        }}
        validationSchema={SignupSchema}
        onSubmit={async (values, actions) => {
          actions.setSubmitting(true)
          const newValues = { ...values }

          const data = encode({ 'form-name': formName, ...newValues, subject: emailSubject })
          try {
            const res = await Axios.post('/', data, {
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            })

            await new Promise(resolve => setTimeout(resolve, 1500))

            if (res.status === 200) {
              if (isGtmForm) {
                window.dataLayer.push({
                  event: 'lead-submission',
                })
              }
              capterraTrackingListener()
              actions.resetForm()
              if (setUserName) setUserName(newValues.firstName)
              actions.setSubmitting(false)
              if (setSubmitted) setSubmitted(true)
              successMessage &&
                setAlertMessage({
                  isOpen: true,
                  message: successMessage,
                  alertType: AlertType.SUCCESS,
                })
            }
            actions.setSubmitting(false)
            callback && callback()
          } catch (err) {
            console.log({ err })
            actions.resetForm()
            setAlertMessage({
              isOpen: true,
              message: errorMessage,
              alertType: AlertType.ERROR,
            })
            actions.setSubmitting(false)
          }
        }}
      >
        {({ isSubmitting, errors, touched }) => (
          <StyledForm data-netlify name={formName} netlify-honeypot='bot-field' method='POST'>
            <input type='hidden' name='form-name' value={formName} />
            <input name='bot-field' type='hidden' />
            {emailSubject && <input type='hidden' name='subject' value={emailSubject} />}
            <Double>
              <InputGroup>
                <Label htmlFor='firstName'>First Name</Label>
                <StyledField
                  whitefields={whitefields ? 1 : 0}
                  id='firstName'
                  type='firstName'
                  name='firstName'
                  autoComplete='name'
                />
                <StyledErrorMessage name='firstName' component='div' />
              </InputGroup>
              <InputGroup>
                <Label htmlFor='lastName'>Last Name</Label>
                <StyledField whitefields={whitefields ? 1 : 0} id='lastName' type='lastName' name='lastName' />
                <StyledErrorMessage name='lastName' component='div' />
              </InputGroup>
            </Double>
            <Double>
              <InputGroup>
                <Label htmlFor='company'>Company</Label>
                <StyledField whitefields={whitefields ? 1 : 0} id='company' type='company' name='company' />
                <StyledErrorMessage name='company' component='div' />
              </InputGroup>
              <InputGroup>
                <Label htmlFor='email'>Email</Label>
                <StyledField
                  whitefields={whitefields ? 1 : 0}
                  id='email'
                  type='email'
                  name='email'
                  autoComplete='email'
                  errors={errors.email && touched.email && true}
                />
                <StyledErrorMessage name='email' component='div' />
              </InputGroup>
            </Double>
            <Double>
              <InputGroup>
                <Label htmlFor='city'>City</Label>
                <StyledField whitefields={whitefields ? 1 : 0} id='city' type='city' name='city' />
                <StyledErrorMessage name='city' component='div' />
              </InputGroup>

              <InputGroup>
                <Label htmlFor='countryRegion'>Country/Region</Label>

                <StyledField
                  whitefields={whitefields ? 1 : 0}
                  id='countryRegion'
                  type='countryRegion'
                  name='countryRegion'
                  component='select'
                >
                  {DropdownElements.map((obj: DropdownElementDetails, idx) => {
                    return (
                      <option key={idx} value={obj.value}>
                        {obj.text}
                      </option>
                    )
                  })}
                  ;
                </StyledField>
                <StyledErrorMessage name='countryRegion' component='div' />
              </InputGroup>
            </Double>
            <Double>
              <InputGroup>
                <Label htmlFor='phone'>Phone</Label>
                <StyledField
                  whitefields={whitefields ? 1 : 0}
                  id='phone'
                  type='phone'
                  name='phone'
                  autoComplete='phone'
                  errors={errors.phone && touched.phone && true}
                />
                <StyledErrorMessage name='phone' component='div' />
              </InputGroup>
              {isSubmitting ? (
                <Button
                  action={<Oval width={14} height={14} />}
                  type='submit'
                  disabled
                  btnHeight='2.5rem'
                  aria-label='submit'
                  bgColor={whitefields ? themeContext.colors.green : themeContext.colors.purple}
                />
              ) : (
                <Button
                  action={action ?? 'submit'}
                  type='submit'
                  disabled={isSubmitting}
                  btnHeight='2.5rem'
                  aria-label='disabled button'
                  bgColor={whitefields ? themeContext.colors.green : themeContext.colors.purple}
                />
              )}
            </Double>
          </StyledForm>
        )}
      </Formik>
    </FormWrapper>
  )
}

const getPaddingOffset = (value: string) => {
  switch (value.length) {
    case 2:
      return '2rem'
    case 3:
      return '3rem'
    case 4:
      return '4rem'
    case 5:
      return '4rem'
    case 6:
      return '4rem'
    default:
      return '3rem'
  }
}

interface CodeProps {
  isOpen: boolean
}
const FormWrapper = styled.div``

export const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
`

export const StyledField = styled(Field)`
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  padding-right: 0.75rem;
  padding-left: ${({ val }) => (val ? getPaddingOffset(val) : '0.75rem')};
  font-size: 0.875rem;
  margin: 0.5rem 0;
  border: none;
  border: ${({ errors, theme }) => (errors ? `1px solid ${theme.colors.error}` : `1px solid ${theme.colors.grey300}`)};
  border-radius: 4px;
  background: ${({ theme, whitefields }) => (whitefields ? '#fff' : theme.colors.grey200)};
  width: 100%;
  transition: border 0.15s ease;
  position: relative;
  -webkit-appearance: none;
  &:focus-within {
    border: 1px solid ${({ theme }) => theme.colors.grey500};
  }
`

export const Double = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: center;

  @media all and (max-width: 36.625em) {
    grid-template-columns: 1fr;
    gap: 0;
  }

  button {
    grid-column: 2/2;
    @media all and (max-width: 36.625em) {
      grid-column: 1/1;
    }
  }
`

export const Label = styled.label`
  font-weight: 600;
  font-size: 0.875rem;
`

export const StyledErrorMessage = styled(ErrorMessage)`
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.error};
  user-select: none;
  position: absolute;
`

interface InputGroupProps {
  raised?: boolean
}

export const InputGroup = styled.div<InputGroupProps>`
  margin: 0.5rem 0 1.5rem;
  position: relative;
  z-index: ${({ raised }) => (raised ? 5 : 1)};
`
